import { useIsTryOnPopupOpenedValue, useSetOpenedPopupsState } from '@/atoms/opened-popups';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSetSelectedServiceValueFormState } from '@/atoms/form/selected-service-value-form-state';
import dynamic from 'next/dynamic';
import Button from '@/components/shared/Button';
import Image from 'next/image';

const Popup = dynamic(() => import('@/components/shared/Popup'));

export const TRY_ON_POPUP_NAME = 'try-on-popup';

// Глобальное состояние для изображения товара
let productImageState: string | null = null;
const imageStateListeners: Set<(image: string | null) => void> = new Set();

// Хук для работы с изображением товара
export const useTryOnProductImage = () => {
    const [productImage, setProductImageState] = useState<string | null>(productImageState);

    const setProductImage = useCallback((image: string | null) => {
        productImageState = image;
        imageStateListeners.forEach((listener) => listener(image));
    }, []);

    useEffect(() => {
        const listener = (image: string | null) => setProductImageState(image);
        imageStateListeners.add(listener);

        return () => {
            imageStateListeners.delete(listener);
        };
    }, []);

    return { productImage, setProductImage };
};

const TryOnPopup = () => {
    const { closePopup } = useSetOpenedPopupsState();
    const isOpened = useIsTryOnPopupOpenedValue();
    const setSelectedServiceValueFormState = useSetSelectedServiceValueFormState();
    const { productImage } = useTryOnProductImage(); // Use the hook to get the product image
    const el = useRef<HTMLDivElement>(null);
    const hint = useRef<HTMLDivElement>(null);
    const closeButton = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!isOpened) {
            setSelectedServiceValueFormState(null);
        }
    }, [isOpened, setSelectedServiceValueFormState]);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [scale, setScale] = useState(1);

    // Функция для остановки камеры
    const stopCamera = () => {
        if (videoRef.current?.srcObject) {
            const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
            tracks.forEach((track) => {
                track.stop();
            });
            videoRef.current.srcObject = null;
        }
    };

    useEffect(() => {
        let stream: MediaStream | null = null;

        const initCamera = async () => {
            try {
                // Конфигурация для задней камеры
                const constraints = {
                    video: {
                        facingMode: { ideal: 'environment' }, // 'environment' = задняя камера
                    },
                };

                stream = await navigator.mediaDevices.getUserMedia(constraints);
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            } catch (err) {
                console.error('Ошибка доступа к камере:', err);

                // Fallback: попробовать любую доступную камеру
                try {
                    stream = await navigator.mediaDevices.getUserMedia({ video: true });
                    if (videoRef.current) {
                        videoRef.current.srcObject = stream;
                    }
                } catch (fallbackErr) {
                    console.error('Не удалось получить доступ ни к одной камере:', fallbackErr);
                }
            }
        };

        const stopCamera = () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
                stream = null;
            }
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
        };

        if (isOpened) {
            initCamera();
        }

        return () => {
            stopCamera();
        };
    }, [isOpened]);

    const increaseScale = () => setScale((prev) => Math.min(prev + 0.1, 3));
    const decreaseScale = () => setScale((prev) => Math.max(prev - 0.1, 0.5));

    return (
        <Popup name={TRY_ON_POPUP_NAME} className="try-on-popup" overlay>
            {isOpened && (
                <div>
                    <div className="try-on-popup__hint" ref={hint}>
                        <Button
                            geometryVariant="square-bracket"
                            onClick={() => {
                                console.log('Нажата кнопка закрытия');
                                stopCamera();
                                closePopup(TRY_ON_POPUP_NAME);
                            }}
                            aria-label="Закрыть окно примерки"
                            className="try-on-popup__close-button close-button"
                            ref={closeButton}
                        >
                            X
                        </Button>
                    </div>
                    <div className="try-on-popup__inner" ref={el}>
                        <div className="try-on-popup__wrapper">
                            <div className="try-on-popup__videoContainer">
                                <video ref={videoRef} autoPlay playsInline className="try-on-popup__video" />
                                <div className="try-on-popup__overlayImageWrap">
                                    {productImage ? (
                                        <Image
                                            className="try-on-popup__overlayImage"
                                            src={productImage}
                                            alt="Product Overlay"
                                            style={{ transform: `scale(${scale})` }}
                                            width={300}
                                            height={300}
                                        />
                                    ) : (
                                        <p>Изображение не задано</p> // Fallback if no image is provided
                                    )}
                                </div>
                            </div>

                            <div className="try-on-popup__controls">
                                <Button
                                    variant="bordered-light"
                                    className="product-trade-in-button control-button text-l"
                                    onClick={increaseScale}
                                >
                                    +
                                </Button>
                                <Button
                                    variant="bordered-light"
                                    className=" product-trade-in-button control-button text-l"
                                    onClick={decreaseScale}
                                >
                                    −
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Popup>
    );
};

// Updated Button Component Example
export const TryOnButton = ({ imageUrl }: { imageUrl: string }) => {
    const { setProductImage } = useTryOnProductImage();
    const { openPopup } = useSetOpenedPopupsState();
    const setServiceValueFormState = useSetSelectedServiceValueFormState();

    return (
        <Button
            geometryVariant="mustache"
            className="text-l fitting-button"
            icon={<Image src="/img/fitting-hand.svg" width={16} height={16} unoptimized alt="" />}
            onClick={() => {
                setServiceValueFormState('Sell');
                setProductImage(imageUrl); // Set the custom image URL
                openPopup(TRY_ON_POPUP_NAME);
            }}
        >
            Примерить онлайн
        </Button>
    );
};

export default TryOnPopup;
